import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { CheckCircle, Clock, XCircle, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentSubmissionModal = ({ open, onClose }) => {
  const [documents, setDocuments] = useState([]);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [optionalDocs, setOptionalDocs] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchDocumentList();
      fetchUploadedDocuments();
    }
  }, [open]);

  const fetchDocumentList = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/required-list`);
      setRequiredDocs(response.data.required);
      setOptionalDocs(response.data.optional);
    } catch (error) {
      console.error('Failed to fetch document list:', error);
    }
  };

  const fetchUploadedDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/detailed`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const handleFileUpload = async (documentName, isRequired, file) => {
    if (!file) return;

    setUploading(true);
    const reader = new FileReader();

    reader.onloadend = async () => {
      try {
        await axios.post(`${API_URL}/api/documents/detailed/upload`, {
          document_name: documentName,
          document_type: file.type,
          file_data: reader.result,
          is_required: isRequired
        });
        toast.success(`${documentName} uploaded successfully!`);
        fetchUploadedDocuments();
      } catch (error) {
        toast.error('Failed to upload document');
      } finally {
        setUploading(false);
      }
    };

    reader.readAsDataURL(file);
  };

  const getDocumentStatus = (docName) => {
    const doc = documents.find(d => d.document_name === docName);
    return doc ? doc.status : null;
  };

  const renderDocumentItem = (docName, index, isRequired) => {
    const status = getDocumentStatus(docName);
    const hasDocument = status !== null;

    return (
      <div key={index} className="p-4 rounded-xl border bg-card hover:border-primary/50 transition-all">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-medium text-foreground">{index + 1}. {docName}</span>
              {isRequired && <span className="text-xs px-2 py-0.5 rounded-full bg-destructive/20 text-destructive">Required</span>}
            </div>
            {hasDocument && (
              <div className="flex items-center gap-2 text-sm">
                {status === 'verified' && <CheckCircle className="h-4 w-4 text-success" />}
                {status === 'pending' && <Clock className="h-4 w-4 text-warning" />}
                {status === 'rejected' && <XCircle className="h-4 w-4 text-destructive" />}
                <span className={`${
                  status === 'verified' ? 'text-success' :
                  status === 'pending' ? 'text-warning' :
                  'text-destructive'
                }`}>
                  {status === 'verified' ? 'Verified' : status === 'pending' ? 'Under Review' : 'Rejected - Please reupload'}
                </span>
              </div>
            )}
          </div>
          <div>
            <label htmlFor={`file-${index}`} className="cursor-pointer">
              <div className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                hasDocument
                  ? 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                  : 'bg-primary text-white hover:bg-primary/90'
              }`}>
                {hasDocument ? 'Reupload' : 'Upload'}
              </div>
              <input
                id={`file-${index}`}
                type="file"
                className="hidden"
                onChange={(e) => handleFileUpload(docName, isRequired, e.target.files[0])}
                disabled={uploading}
              />
            </label>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading font-semibold text-2xl">Document Submission</DialogTitle>
          <p className="text-muted-foreground">Upload all required documents in order</p>
        </DialogHeader>

        <Tabs defaultValue="required" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="required">Required Documents</TabsTrigger>
            <TabsTrigger value="optional">Optional Information</TabsTrigger>
          </TabsList>

          <TabsContent value="required" className="space-y-3 mt-4">
            {requiredDocs.map((doc, index) => renderDocumentItem(doc, index, true))}
          </TabsContent>

          <TabsContent value="optional" className="space-y-3 mt-4">
            {optionalDocs.map((doc, index) => renderDocumentItem(doc, index, false))}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentSubmissionModal;